import React, { useEffect, useState } from "react";
import { Form, Button, Modal, Popconfirm, Row, Col, Dropdown, Space } from "antd";
import { ReactSVG } from "react-svg";
import { useDispatch, useSelector } from "react-redux";
import socketIOClient from "socket.io-client";

import ListContainer from "../../modules/contact/containers/List";
import ContactsTable from "../Table/Contacts";
import ContactForm from "../Forms/Contacts/Contact";
import ContactsFilterForm from "../Forms/Filters/Contacts";
import withPage from "./withPage";
import RoutePlanCurrent from "../RoutePlan/RoutePlanCurrent";
import SettingMenu from "../Menu/SettingMenu";

import { DATE_FORMAT_QUERY, PAGE_SIZE, TIME_FORMAT_FRONTEND } from "../../constants";
import { getTranslation } from "../../utils/helper";
import "./ContactLocations.scss";
import BulkDeleteForm from "../Forms/Contacts/BulkDelete";
import { selectAllRoutePlans } from "../../modules/routePlan/stores/slice";
import BulkAddToRoutePlanForm from "../Forms/Orders/BulkAddToRoutePlan";
import { doCreateMutiOrders } from "../../modules/order/stores/thunk";
import RoutePlanForm from "../Forms/RoutePlans/RoutePlan";
import { doCreateRoutePlanNonRedirect } from "../../modules/routePlan/stores/thunk";
import Cookies from "js-cookie";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

// Extend Day.js with UTC plugin
dayjs.extend(utc);

const socketURI = process.env.REACT_APP_SOCKET_URI || "";
const socket = socketIOClient(socketURI);

const A_DELETE = "A_DELETE";
const A_ADD_ORDER_TO_NEW_ROUTE = "A_ADD_ORDER_TO_NEW_ROUTE";
const A_ADD_ORDER_TO_EXIST_ROUTE = "A_ADD_ORDER_TO_EXIST_ROUTE";
const defaultCountryCode = window.location.href.includes("nordicrouting.se") ? "se" : "sg";

const ContactLocationsPage = ({
  isFetching,
  status,
  contacts,
  metadata,
  query,
  pushTo,
  doGetAllContacts,
  doCreateContact,
  doUpdateContact,
  doDeleteContact,
  doDeleteContacts,
  message,
  history,
  id,
  routePlan,
  orders,
  metadataOrders,
  doGetAllOrders,
  doUploadOrders,
  doAddOrdersRoutePlan,
  handleFailedOptimize,
  user,
}) => {
  const dispatch = useDispatch();
  const [showFilters, setShowFilters] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const [defaultSelectedItems, setDefaultSelectedItems] = useState([]);
  const [modifying, setModifying] = useState(false);
  const [newDeliveryDate, setNewDeliveryDate] = useState();
  const [selectedContacts] = useState([]);

  const setting = useSelector(state => state.setting.setting) || user.company;
  const routePlans = selectAllRoutePlans(useSelector(state => state));
  const stateRoutePlan = useSelector(state => state.routePlan);
  const isFetchingOrder = useSelector(state => state.order.isFetching);
  const messageRoutePlan = stateRoutePlan.message;
  const isFetchingRoute = stateRoutePlan.isFetching;
  const isFetchingOther = isFetchingRoute || isFetchingOrder;

  const [settingType, setSettingType] = useState("contact-list");

  const [countryCodeInit, setCountryCodeInit] = useState(defaultCountryCode);

  useEffect(() => {
    setCountryCodeInit(Cookies.get(`CountryCode-${user._id}`) || defaultCountryCode);
  }, []);

  const [deleteForm] = Form.useForm();
  const [addToRouteForm] = Form.useForm();
  const [newRouteForm] = Form.useForm();
  const [contactForm] = Form.useForm();

  const currentPage = Number(query.currentPage) || 1;
  const itemsPerPage = Number(query.itemsPerPage) || PAGE_SIZE;
  const pagination = {
    current: currentPage,
    pageSize: itemsPerPage,
    total: metadata.totalItems,
    showTotal: total => `${total} ${getTranslation(setting, "Contacts", "Contacts")}`,
  };

  useEffect(() => {
    if (!isFetchingRoute && messageRoutePlan?.includes("create_route_plan_succeeded_") && newDeliveryDate) {
      callbackCreateRoutePlan();
    }
  }, [isFetchingRoute, messageRoutePlan]);

  const [state, setState] = useState({
    visibleModal: false,
    action: "",
    contact: null,
    items: [],
  });

  const ACTIONS = [
    [A_DELETE, getTranslation(setting, "Contacts", "Delete"), "delete"],
    [
      A_ADD_ORDER_TO_NEW_ROUTE,
      getTranslation(setting, "Contacts", "Create New Route Plan with Selected Contacts"),
      "add",
    ],
    [A_ADD_ORDER_TO_EXIST_ROUTE, getTranslation(setting, "Contacts", "Add to Existing Route Plan"), "add-circle"],
  ];

  useEffect(() => {
    if (!isFetching && ["bulk_delete_succeeded", "bulk_update_succeeded"].includes(message)) {
      handleCancel();
      setTimeout(() => {
        if (message === "bulk_update_succeeded") {
          doGetAllOrders({ currentPage, itemsPerPage, ...query });
        }
      }, 2000);
    }
  }, [isFetching, message]);

  useEffect(() => {
    if (metadata?.allContacts?.length > 0) {
      setDefaultSelectedItems(metadata.allContacts);
      setState({ ...state, items: metadata.allContacts });
    }
  }, [metadata]);

  useEffect(() => {
    socket.connect();
    socket.on(user.company?._id, data => {
      if (data.event === "contactLocations.geocoding.completed") {
        doGetAllContacts({ currentPage, itemsPerPage, ...query });
      }
    });
    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (!isFetching && status === "succeeded") {
      handleCancel();
    }
  }, [isFetching]);

  useEffect(() => {
    doGetAllContacts({ currentPage, itemsPerPage, ...query });
  }, []);

  const handleFilter = (params, updatePath) => {
    if (updatePath) {
      pushTo({ query: params });
    }
    const currentPage = Number(params.currentPage) || 1;
    const itemsPerPage = Number(params.itemsPerPage) || PAGE_SIZE;
    doGetAllContacts({
      currentPage,
      itemsPerPage,
      ...params,
    });
  };

  const callbackCreateRoutePlan = async () => {
    const id = messageRoutePlan?.split("_")[4];
    const params = [];
    state.items.forEach(contact => {
      params.push({
        deliveryName: contact.name,
        deliveryPhone: contact.phone,
        deliveryTimeStart: contact.startTime,
        deliveryTimeEnd: contact.endTime,
        deliveryDuration: contact.duration,
        deliveryAddress: contact.address,
        deliveryLat: contact.lat,
        deliveryLng: contact.lng,
        requiredSkills: contact.types,
        deliveryNotes: contact.notes,
        load: contact.load,
        fields: contact.fields,
        routePlan: id,
        deliveryDate: newDeliveryDate,
        routePlanType: "VRP",
      });
    });
    await dispatch(doCreateMutiOrders(params));
    setModifying(false);
    history.push(`/route-plans/${id}/orders`);
  };

  const handleSelectAll = async () => {
    // setDefaultSelectedItems(metadata.allOrders);
    // setState({ ...state, items: metadata.allOrders });
    const value = !isSelectAll;
    setIsSelectAll(value);
    if (value) {
      await doGetAllContacts({
        currentPage,
        itemsPerPage,
        ...query,
        isSelectAll: value,
      });
    } else {
      setDefaultSelectedItems([]);
      setState({ ...state, items: [] });
    }
  };

  const handleBulkUpdate = selectedContacts => {
    return {
      title: [
        <Button
          key={"select-all"}
          type={isSelectAll ? "primary" : "default"}
          className="btn-select-all"
          onClick={handleSelectAll}>
          {getTranslation(setting, "Common", "Select all")}
        </Button>,
        `${selectedContacts.length} ${getTranslation(setting, "Contacts", "selected")}`,
      ],
      actions: (
        <Space>
          {ACTIONS.map(action => (
            <Button
              className={`btn-bulk-${action[2]}`}
              key={`bulk-${action[2]}`}
              onClick={() => handleBulkActions(selectedContacts, action[0])}
              type="primary"
              disabled={selectedContacts.length <= 0}>
              {action[1]}
              <ReactSVG className="icon" src={`/icons/bulk-${action[2]}.svg`} />
            </Button>
          ))}
        </Space>
      ),
    };
  };

  const handleBulkActions = (selectedContacts, action) => {
    setState({ ...state, visibleModal: true, action, items: selectedContacts });
    if (action === "A_ADD_ORDER_TO_NEW_ROUTE") {
      contactForm.setFieldsValue({ type: "VRP" });
    }
  };

  const handleTableChange = params => {
    const newParams = { ...query, ...params };
    handleFilter(newParams, true);
  };

  const withAction = item => {
    return (
      <div className="actions">
        <Button
          type="link"
          className="edit"
          onClick={() => {
            handleClickAction("edit", item);
          }}>
          <ReactSVG className="icon" src="/icons/edit.svg" />
        </Button>
        <Popconfirm
          title={getTranslation(setting, "Common", "Are you sure to delete this item?")}
          onConfirm={() => handleClickDelete(item)}
          okText={getTranslation(setting, "Common", "Yes")}
          cancelText={getTranslation(setting, "Common", "No")}>
          <Button type="link" className="delete">
            <ReactSVG className="icon" src="/icons/delete.svg" />
          </Button>
        </Popconfirm>
      </div>
    );
  };

  const handleClickDelete = async item => {
    await doDeleteContact(item._id);
    await doGetAllContacts({ currentPage, itemsPerPage, ...query });
  };

  const handleClickAction = (action, item) => {
    contactForm.resetFields();
    const params = {
      ...item,
      startTime: item?.startTime ? dayjs(item?.startTime, TIME_FORMAT_FRONTEND) : null,
      endTime: item?.endTime ? dayjs(item?.endTime, TIME_FORMAT_FRONTEND) : null,
      address: {
        address: item?.address,
        latLng: { lat: item?.lat, lng: item?.lng },
      },
    };
    const fields = item?.fields || [];
    params.fields = Object.keys(fields).map(key => ({
      name: key,
      value: fields[key],
    }));
    contactForm.setFieldsValue(params);
    setState({
      ...state,
      action,
      contact: item,
      visibleModal: true,
    });
  };

  const handleSubmit = async values => {
    const params = setParams(values);
    if (state.contact) {
      await doUpdateContact({ id: state.contact._id, ...params });
    } else {
      await doCreateContact(params);
      await doGetAllContacts({ currentPage, itemsPerPage, ...query });
    }
  };

  const handleSubmitMultipleAction = async values => {
    setModifying(true);
    if (state.action === "A_DELETE") {
      await doDeleteContacts({ ids: state.items.map(s => s._id) });
      setModifying(false);
    } else if (state.action === A_ADD_ORDER_TO_EXIST_ROUTE) {
      const params = [];
      const routePlan = routePlans.find(r => r._id === values.routePlanId);
      const deliveryDateQuery = dayjs(routePlan?.deliveryDate?.format()).format(DATE_FORMAT_QUERY);
      selectedContacts.forEach(contact => {
        params.push({
          deliveryName: contact.name,
          deliveryPhone: contact.phone,
          deliveryTimeStart: contact.startTime,
          deliveryTimeEnd: contact.endTime,
          deliveryDuration: contact.duration,
          deliveryAddress: contact.address,
          deliveryLat: contact.lat,
          deliveryLng: contact.lng,
          requiredSkills: contact.types,
          deliveryNotes: contact.notes,
          load: contact.load,
          fields: contact.fields,
          routePlan: routePlan?._id,
          deliveryDate: deliveryDateQuery,
          routePlanType: "VRP",
        });
      });
      await dispatch(doCreateMutiOrders(params));
      setModifying(false);
      history.push(`/route-plans/${routePlan?._id}/orders`);
      handleCancel();
    } else if (state.action === A_ADD_ORDER_TO_NEW_ROUTE) {
      setNewDeliveryDate(values.deliveryDate);
      dispatch(doCreateRoutePlanNonRedirect(values));
    }
  };

  const handleCancel = () => {
    setState({ ...state, visibleModal: false });
    // Only reset the form that's currently active
    switch (state.action) {
      case "A_DELETE":
        deleteForm.resetFields();
        break;
      case "A_ADD_ORDER_TO_EXIST_ROUTE":
        addToRouteForm.resetFields();
        break;
      case "A_ADD_ORDER_TO_NEW_ROUTE":
        newRouteForm.resetFields();
        break;
      case "new":
      case "edit":
        contactForm.resetFields();
        break;
      default:
        break;
    }
  };

  const setParams = values => {
    const params = { ...values };
    params.address = values.address?.address;
    params.lat = values.address?.latLng?.lat;
    params.lng = values.address?.latLng?.lng;
    return params;
  };

  const handleClickNewContacts = type => {
    if (type === "upload") {
      history.push("/settings/contact-list/upload");
    }
    if (type === "new") {
      setState({ ...state, visibleModal: true, action: type });
    }
  };

  return (
    <div className="Content SettingsPage">
      <RoutePlanCurrent />
      <div className="ContentMain">
        <Row gutter={16}>
          <Col span={6}>
            <SettingMenu
              history={history}
              setting={setting}
              settingType={settingType}
              setSettingType={setSettingType}
            />
          </Col>
          <Col span={18}>
            <div className="SettingContent">
              <div className="ContactLocationsPage">
                <div className="ContentHeader">
                  <div className="HeaderTitle">{getTranslation(setting, "Contacts", "Contact List")}</div>
                  <div className="HeaderToolBar">
                    <div className="ToolBarButtons">
                      <Button
                        className={`FilterButton ${showFilters ? "Open" : "Close"}`}
                        type="primary"
                        onClick={() => setShowFilters(!showFilters)}>
                        {getTranslation(setting, "Common", "Filter")}
                        <ReactSVG className="icon" src={`/icons/filter${showFilters ? "-close" : ""}.svg`} />
                      </Button>
                      <Dropdown
                        menu={{
                          items: [
                            {
                              key: "upload",
                              label: (
                                <Button type="link" onClick={() => handleClickNewContacts("upload")}>
                                  {getTranslation(setting, "Contacts", "Upload")}
                                  <ReactSVG className="icon" src="/icons/upload.svg" />
                                </Button>
                              ),
                            },
                            {
                              key: "new",
                              label: (
                                <Button type="link" onClick={() => handleClickNewContacts("new")}>
                                  {getTranslation(setting, "Contacts", "Single Contact")}
                                  <ReactSVG className="icon" src="/icons/manual.svg" />
                                </Button>
                              ),
                            },
                          ],
                        }}
                        placement="bottomRight">
                        <Button size="large" className="NewButton" type="primary">
                          {getTranslation(setting, "Contacts", "New Contact")}
                          <ReactSVG className="icon" src="/icons/add.svg" />
                        </Button>
                      </Dropdown>
                    </div>
                  </div>
                </div>
                <div className="ContentFilters">
                  {showFilters && <ContactsFilterForm isFetching={isFetching} query={query} onSubmit={handleFilter} />}
                </div>
                <div className="ContentMain">
                  <ContactsTable
                    rowKey="_id"
                    loading={isFetching || isFetchingOther}
                    query={query}
                    withAction={withAction}
                    pagination={pagination}
                    dataSource={contacts}
                    onChange2={handleTableChange}
                    scrollHeight="calc(100vh - 285px)"
                    actionWidth={120}
                    setting={setting}
                    bulkUpdate={handleBulkUpdate}
                    defaultSelectedItems={defaultSelectedItems}
                    setDefaultSelectedItems={setDefaultSelectedItems}
                    modifying={modifying}
                    isSortLocal={false}
                  />
                </div>
                <Modal open={state.visibleModal} onCancel={handleCancel} footer={[]} forceRender width={792}>
                  <>
                    {state.action === "A_DELETE" && (
                      <BulkDeleteForm
                        form={deleteForm}
                        isFetching={isFetching}
                        items={state.items}
                        handleSubmit={handleSubmitMultipleAction}
                        handleCancel={handleCancel}
                      />
                    )}
                    {state.action === "A_ADD_ORDER_TO_EXIST_ROUTE" && (
                      <BulkAddToRoutePlanForm
                        form={addToRouteForm}
                        isFetching={isFetching || isFetchingOther}
                        handleSubmit={handleSubmitMultipleAction}
                        handleCancel={handleCancel}
                      />
                    )}
                    {state.action === "A_ADD_ORDER_TO_NEW_ROUTE" && (
                      <RoutePlanForm
                        form={newRouteForm}
                        isFetching={isFetching || isFetchingOther}
                        routePlan={null}
                        handleSubmit={handleSubmitMultipleAction}
                        handleCancel={handleCancel}
                        createWithContacts={true}
                      />
                    )}
                    {["new", "edit"].includes(state.action) && (
                      <ContactForm
                        form={contactForm}
                        isFetching={isFetching}
                        contact={state.contact}
                        handleSubmit={handleSubmit}
                        handleCancel={handleCancel}
                        countryCodeInit={countryCodeInit}
                      />
                    )}
                  </>
                </Modal>
              </div>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default withPage(ListContainer(ContactLocationsPage));
